import { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../lib/auth-context';
import { RivlLogo } from '../components/common/RivlLogo';
import { ThemeToggle } from '../components/common/ThemeToggle';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { ArrowLeft, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

export default function Auth() {
  const { signIn, signUp, supabaseEnabled } = useAuth();
  const nav = useNavigate();
  const { state } = useLocation();
  const [tab, setTab] = useState('signin');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [birthday, setBirthday] = useState(''); // yyyy-mm-dd
  const [role, setRole] = useState('runner'); // 'runner' | 'organizer'
  const [busy, setBusy] = useState(false);

  const redirectTo = state?.from?.pathname || '/';

  async function handleSignIn(e) {
    e.preventDefault();
    if (!isValidEmail(email)) {
      toast.error('Enter a valid email');
      return;
    }
    if (!password) {
      toast.error('Enter your password');
      return;
    }
    setBusy(true);
    try {
      await signIn(email, password);
      toast.success('Welcome back');
      nav(redirectTo, { replace: true });
    } catch (err) {
      toast.error(err.message || 'Sign in failed');
    } finally {
      setBusy(false);
    }
  }

  async function handleSignUp(e) {
    e.preventDefault();
    if (!firstName.trim() || !lastName.trim()) {
      toast.error('Please enter your first and last name');
      return;
    }
    if (!isValidEmail(email)) {
      toast.error('Enter a valid email');
      return;
    }
    if (!birthday) {
      toast.error('Please enter your birthday');
      return;
    }
    const age = ageFromBirthday(birthday);
    if (age == null || age < 13) {
      toast.error('You must be at least 13 years old to sign up');
      return;
    }
    if (password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    setBusy(true);
    try {
      const fullName = `${firstName.trim()} ${lastName.trim()}`;
      await signUp(email, password, fullName, role, {
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        birthday,
      });
      toast.success("Account created — you're in.");
      nav(redirectTo, { replace: true });
    } catch (err) {
      const msg = (err.message || '').toLowerCase();
      if (msg.includes('rate limit') || msg.includes('email rate')) {
        toast.error(
          'Supabase email rate limit hit. Fix: Dashboard → Authentication → Providers → Email → turn OFF "Confirm email" (dev), OR raise the rate limit under Authentication → Rate Limits.',
          { duration: 12000, id: 'signup-ratelimit' }
        );
      } else if (msg.includes('email address') && msg.includes('invalid')) {
        toast.error('Email address rejected by Supabase. Use a real domain (gmail.com, outlook.com) — Supabase blocks .test/.local/.example TLDs.', { duration: 8000 });
      } else if (msg.includes('already registered') || msg.includes('user already')) {
        toast.error('An account with this email already exists. Try signing in instead.');
      } else {
        toast.error(err.message || 'Sign up failed');
      }
    } finally {
      setBusy(false);
    }
  }

  const passwordTooShort = password.length > 0 && password.length < 6;

  return (
    <div className="min-h-screen bg-background text-foreground rivl-noise flex flex-col">
      <header className="border-b border-border">
        <div className="mx-auto max-w-md px-6 py-4 flex items-center justify-between">
          <Link to="/welcome" className="flex items-center gap-2 text-sm text-muted-foreground" data-testid="auth-back">
            <ArrowLeft className="h-4 w-4" />
          </Link>
          <RivlLogo size="lg" variant="wordmark" />
          <ThemeToggle />
        </div>
        <div className="h-1 flex">
          <div className="flex-1 bg-primary" />
          <div className="flex-1 bg-card" />
          <div className="flex-1 bg-brand-blue" />
        </div>
      </header>

      <main className="flex-1 flex items-center justify-center px-6 py-10">
        <div className="w-full max-w-md">
          {!supabaseEnabled && (
            <div className="rivl-card p-4 mb-4 border-primary text-sm" data-testid="auth-no-supabase">
              <div className="font-display font-semibold text-primary">Supabase not configured</div>
              <p className="text-muted-foreground mt-1 text-xs">
                Add <span className="font-mono">REACT_APP_SUPABASE_URL</span> and <span className="font-mono">REACT_APP_SUPABASE_ANON_KEY</span> to your <span className="font-mono">.env</span> file and restart. The app currently runs in mock mode — auth is bypassed.
              </p>
            </div>
          )}

          <div className="text-center mb-6">
            <p className="rivl-label">Welcome to RIVL</p>
            <h1 className="font-display text-3xl font-bold tracking-tight mt-1">
              {tab === 'signin' ? 'Sign in' : 'Create your account'}
            </h1>
          </div>

          <Tabs value={tab} onValueChange={setTab} className="w-full">
            <TabsList className="grid grid-cols-2 h-auto p-1 rounded-md w-full">
              <TabsTrigger value="signin" data-testid="auth-tab-signin" className="text-xs uppercase tracking-widest">Sign in</TabsTrigger>
              <TabsTrigger value="signup" data-testid="auth-tab-signup" className="text-xs uppercase tracking-widest">Sign up</TabsTrigger>
            </TabsList>

            <TabsContent value="signin" className="mt-6">
              <form onSubmit={handleSignIn} className="rivl-card p-5 space-y-4">
                <Field label="Email">
                  <Input
                    data-testid="signin-email"
                    type="email"
                    autoComplete="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="h-11 rounded-md"
                    placeholder="alex@rivl.app"
                  />
                </Field>
                <Field label="Password">
                  <Input
                    data-testid="signin-password"
                    type="password"
                    autoComplete="current-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    className="h-11 rounded-md"
                  />
                </Field>
                <Button type="submit" disabled={busy || !supabaseEnabled} className="w-full h-11 rounded-md" data-testid="signin-submit">
                  {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Sign in'}
                </Button>
              </form>
            </TabsContent>

            <TabsContent value="signup" className="mt-6">
              <form onSubmit={handleSignUp} className="rivl-card p-5 space-y-4">
                <Field label="I'm joining as">
                  <div className="grid grid-cols-2 gap-2">
                    <button
                      type="button"
                      onClick={() => setRole('runner')}
                      data-testid="signup-role-runner"
                      className={`h-12 rounded-md border-2 flex flex-col items-center justify-center transition-colors ${
                        role === 'runner' ? 'border-primary bg-primary/5 text-primary' : 'border-border'
                      }`}
                    >
                      <span className="font-display font-bold text-sm">Runner</span>
                      <span className="text-[9px] uppercase tracking-widest text-muted-foreground">join races · log runs</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => setRole('organizer')}
                      data-testid="signup-role-organizer"
                      className={`h-12 rounded-md border-2 flex flex-col items-center justify-center transition-colors ${
                        role === 'organizer' ? 'border-primary bg-primary/5 text-primary' : 'border-border'
                      }`}
                    >
                      <span className="font-display font-bold text-sm">Organizer</span>
                      <span className="text-[9px] uppercase tracking-widest text-muted-foreground">create · run events</span>
                    </button>
                  </div>
                </Field>
                <div className="grid grid-cols-2 gap-3">
                  <Field label="First name">
                    <Input
                      data-testid="signup-first-name"
                      value={firstName}
                      onChange={(e) => setFirstName(e.target.value)}
                      required
                      className="h-11 rounded-md"
                      placeholder="Alex"
                    />
                  </Field>
                  <Field label="Last name">
                    <Input
                      data-testid="signup-last-name"
                      value={lastName}
                      onChange={(e) => setLastName(e.target.value)}
                      required
                      className="h-11 rounded-md"
                      placeholder="Mercer"
                    />
                  </Field>
                </div>
                <Field label="Email">
                  <Input
                    data-testid="signup-email"
                    type="email"
                    autoComplete="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="h-11 rounded-md"
                  />
                </Field>
                <Field label="Birthday">
                  <Input
                    data-testid="signup-birthday"
                    type="date"
                    value={birthday}
                    onChange={(e) => setBirthday(e.target.value)}
                    required
                    max={new Date().toISOString().slice(0, 10)}
                    className="h-11 rounded-md"
                  />
                  {birthday && ageFromBirthday(birthday) != null && (
                    <p className="text-[11px] text-muted-foreground mt-1" data-testid="signup-age-hint">
                      Age: {ageFromBirthday(birthday)}
                    </p>
                  )}
                </Field>
                <Field label="Password">
                  <Input
                    data-testid="signup-password"
                    type="password"
                    autoComplete="new-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    minLength={6}
                    className="h-11 rounded-md"
                    placeholder="At least 6 characters"
                    aria-invalid={passwordTooShort || undefined}
                  />
                  {passwordTooShort && (
                    <p className="text-[11px] text-destructive mt-1" data-testid="signup-password-hint">
                      Must be at least 6 characters.
                    </p>
                  )}
                </Field>
                <Button type="submit" disabled={busy || !supabaseEnabled} className="w-full h-11 rounded-md" data-testid="signup-submit">
                  {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Create account'}
                </Button>
                <p className="text-[11px] text-muted-foreground text-center">
                  By signing up you agree to RIVL's terms of service.
                </p>
              </form>
            </TabsContent>
          </Tabs>
        </div>
      </main>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div className="space-y-1.5">
      <Label className="rivl-label">{label}</Label>
      {children}
    </div>
  );
}

function isValidEmail(e) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((e || '').trim());
}

// Returns whole-year age from yyyy-mm-dd, or null if unparseable / future date.
function ageFromBirthday(yyyy_mm_dd) {
  if (!yyyy_mm_dd) return null;
  const dob = new Date(yyyy_mm_dd);
  if (Number.isNaN(dob.getTime())) return null;
  const now = new Date();
  let age = now.getFullYear() - dob.getFullYear();
  const m = now.getMonth() - dob.getMonth();
  if (m < 0 || (m === 0 && now.getDate() < dob.getDate())) age--;
  if (age < 0 || age > 130) return null;
  return age;
}
